import type { SpriteSheetSpec } from '../characters/types';

// Asset indirection layer (pure). Designers upload replacement sprites via the
// studio portal; the game resolves every asset URL through here. The lookup key
// is the asset's ORIGINAL bundled path (e.g. the literal string declared in a
// skill/character bundle) - that path doubles as the stable logical id, so the
// bundle files never need to change.
//
// This module holds no IO: overridesSource.ts fetches the override map (from
// Firestore) and pushes it in via setAssetOverrides. When the map is empty
// (no backend, or nothing overridden) every resolve returns the default, so
// the game behaves exactly as before.

// One override for a logical asset, keyed by its default bundled URL.
// cols/rows/autoTrim only apply to sprite sheets - an uploaded sheet may use a
// different grid than the original, and slicing with the wrong grid breaks the
// animation, so the new grid travels with the override.
export type AssetOverride = {
  url: string;
  cols?: number;
  rows?: number;
  autoTrim?: boolean;
};

let overrides: ReadonlyMap<string, AssetOverride> = new Map();

export function setAssetOverrides(map: ReadonlyMap<string, AssetOverride>): void {
  overrides = map;
}

export function getAssetOverrides(): ReadonlyMap<string, AssetOverride> {
  return overrides;
}

// Resolve a plain texture / audio URL - returns the uploaded URL if one exists.
export function resolveAssetUrl(defaultUrl: string): string {
  return overrides.get(defaultUrl)?.url ?? defaultUrl;
}

// Resolve a sprite-sheet spec - swaps url + grid when overridden. Grid fields
// fall back to the original spec so an image-only override (same grid) still
// works. The original spec.url stays the lookup key.
export function resolveSheetSpec(spec: SpriteSheetSpec): SpriteSheetSpec {
  const o = overrides.get(spec.url);
  if (!o) return spec;
  return {
    url: o.url,
    cols: o.cols ?? spec.cols,
    rows: o.rows ?? spec.rows,
    autoTrim: o.autoTrim ?? spec.autoTrim,
  };
}
